import { Controller } from 'stimulus';

/**
 * Shows a loading state while a form is being submitted.
 *
 * @target submitButton - The submit button element
 * @target buttonText - Optional button text to replace while submitting
 * @target spinner - The spinner element to show/hide inside the button
 * @target status - Optional status message to show/hide while submitting
 */
export default class extends Controller {
  static targets = ['submitButton', 'buttonText', 'spinner', 'status'];
  static values = { submittingText: String };

  connect() {
    this.isSubmitting = false;
    this.originalButtonText = this.hasButtonTextTarget
      ? this.buttonTextTarget.textContent
      : null;

    // Turbo form events fire on the form element and bubble up
    // We listen on document and check if the event is from our form
    document.addEventListener('turbo:submit-start', this.handleSubmitStart);
    document.addEventListener('turbo:submit-end', this.handleSubmitEnd);
    document.addEventListener('turbo:frame-load', this.handleFrameLoad);
  }

  disconnect() {
    document.removeEventListener('turbo:submit-start', this.handleSubmitStart);
    document.removeEventListener('turbo:submit-end', this.handleSubmitEnd);
    document.removeEventListener('turbo:frame-load', this.handleFrameLoad);
  }

  handleSubmitStart = (event) => {
    // Check if this submit event is from a form that contains our submit button
    if (this.isRelevantSubmit(event)) {
      this.isSubmitting = true;
      this.showSpinner();
    }
  };

  handleSubmitEnd = (event) => {
    if (this.isRelevantSubmit(event)) {
      this.isSubmitting = false;
      this.hideSpinner();
    }
  };

  handleFrameLoad = () => {
    // Hide spinner when frame loads (form submission completed)
    if (!this.isSubmitting) this.hideSpinner();
  };

  isRelevantSubmit(event) {
    // Check if the submitted form contains our submit button
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return false;

    return form.contains(this.submitButtonTarget);
  }

  showSpinner() {
    if (!this.hasSubmitButtonTarget) return;

    this.submitButtonTarget.disabled = true;

    // Show spinner if target exists
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove('d-none');
    }

    if (this.hasButtonTextTarget && this.hasSubmittingTextValue) {
      this.buttonTextTarget.textContent = this.submittingTextValue;
    }

    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove('d-none');
    }
  }

  hideSpinner() {
    if (!this.hasSubmitButtonTarget) return;

    this.submitButtonTarget.disabled = false;

    // Hide spinner if target exists
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('d-none');
    }

    if (this.hasButtonTextTarget && this.originalButtonText !== null) {
      this.buttonTextTarget.textContent = this.originalButtonText;
    }

    if (this.hasStatusTarget) {
      this.statusTarget.classList.add('d-none');
    }
  }
}
